/**
 * =============================================================================
 * USE MEDIA AUTHORING HOOK (ADR-022 §10(A) — PR4b, BL-IMG-ADMIN-AUTHORING-001)
 * =============================================================================
 *
 * Thin, toast-free wrappers over the two MEDIA_AUTHOR upload endpoints
 * (`adminApi.media.uploadQuestionImage` / `uploadReplyImage`) shared by every
 * authoring mount (question-detail modal, staff-reply composer attach, RFI
 * post-create attach). Each wrapper resolves to the AdminImageUploader's
 * `{ success, message?, media? }` per-file outcome shape — the uploader
 * renders the failure reason inline against the file row (no toast is fired
 * here), and a success carries the created DTO so callers can append it to
 * local state instead of refetching per file (fold E1).
 *
 * Also the home of the shared FORBIDDEN / legal-hold message mapping for the
 * media authoring + management surfaces (STAFF_REPLY_FORBIDDEN_MESSAGE /
 * RFI_FORBIDDEN_MESSAGE precedent): the worker gates uploads behind the
 * MEDIA_AUTHOR capability and vetoes destructive/republish actions on
 * legal-held rows with a 409 carrying `details.legalHold: true`
 * (D-LEGALHOLD).
 *
 * =============================================================================
 */

import { useCallback, useMemo } from 'react';
import type { AdminMediaListItem } from '@find-my-part/types';
import { adminApi } from '../api/client';

/**
 * Context-specific message for a FORBIDDEN response on the staff image-upload
 * paths (worker enforces `MEDIA_AUTHOR` — a capability distinct from
 * MEDIA_MODERATE, so a moderator without authoring rights can hit this).
 */
export const MEDIA_AUTHOR_FORBIDDEN_MESSAGE =
  'You do not have permission to upload images.';

/**
 * User-facing explanation for a D-LEGALHOLD 409 veto (SHOW / remove on a held
 * row). The hold itself is released via the runbook, never from this UI.
 */
export const MEDIA_LEGAL_HOLD_MESSAGE =
  'This image is preserved under a legal hold and cannot be removed or re-published.';

/** Fallback for an upload failure with no usable server message. */
const UPLOAD_FALLBACK_MESSAGE = 'Failed to upload the image. Please try again.';

/**
 * Narrow error shape of the `{ success: false, error }` envelope this module
 * maps (the full `ApiErrorResponse['error']` minus fields it never reads).
 */
interface MediaApiError {
  code: string;
  message?: string;
  details?: Record<string, unknown>;
}

/** True when a 409 envelope carries the D-LEGALHOLD veto marker. */
export function isLegalHoldError(error: MediaApiError): boolean {
  return (
    error.code === 'INVALID_STATE_TRANSITION' && error.details?.legalHold === true
  );
}

/** Options for {@link resolveMediaErrorMessage} (fold R4). */
export interface ResolveMediaErrorMessageOptions {
  /** Fallback when the envelope carries no usable server message. */
  fallback?: string;
  /**
   * Context-specific FORBIDDEN copy (each surface guards a different
   * capability — MEDIA_AUTHOR wording is wrong on a MEDIA_MODERATE action).
   * Omitted → FORBIDDEN falls through to the server message / fallback like
   * any other code.
   */
  forbiddenMessage?: string;
}

/**
 * Resolve the user-facing message for a media authoring/management error
 * envelope (the shared mapping every media call site funnels through — fold
 * R4). FORBIDDEN → the caller's `forbiddenMessage` when one is given; a
 * legal-hold 409 → the explicit hold explanation; otherwise the server
 * message is preferred (the worker's VALIDATION_ERROR messages carry the
 * actionable remedy — caps, size, format, quota — and this is an admin
 * surface, so no anti-tip-off constraint applies).
 */
export function resolveMediaErrorMessage(
  error: MediaApiError,
  options: ResolveMediaErrorMessageOptions = {}
): string {
  const { fallback = UPLOAD_FALLBACK_MESSAGE, forbiddenMessage } = options;
  if (error.code === 'FORBIDDEN' && forbiddenMessage !== undefined) {
    return forbiddenMessage;
  }
  if (isLegalHoldError(error)) {
    return MEDIA_LEGAL_HOLD_MESSAGE;
  }
  const serverMessage = error.message?.trim();
  return serverMessage || fallback;
}

/** Per-file upload outcome consumed by AdminImageUploader's status list. */
export interface MediaUploadOutcome {
  success: boolean;
  message?: string;
  /**
   * The created media DTO from a successful upload (`{ media }` — the server
   * returns the full admin queue row precisely so callers can land it in
   * local state without a per-file refetch, fold E1).
   */
  media?: AdminMediaListItem;
}

/**
 * Return value of the useMediaAuthoring hook.
 */
export interface UseMediaAuthoringResult {
  /** Upload one image to a question (management add). */
  uploadQuestionImage: (questionId: string, file: File) => Promise<MediaUploadOutcome>;
  /** Upload one image to a STAFF-authored reply / RFI anchor. */
  uploadReplyImage: (replyId: string, file: File) => Promise<MediaUploadOutcome>;
}

/**
 * Hook exposing the two staff image-upload wrappers.
 *
 * No in-flight guard lives here on purpose: AdminImageUploader serializes its
 * per-file uploads behind its own synchronous batch guard, and each call is
 * `maxRetries: 0` at the client layer — the hook stays a pure outcome mapper.
 *
 * @example
 * ```tsx
 * const { uploadReplyImage } = useMediaAuthoring(token);
 * <AdminImageUploader onUpload={(file) => uploadReplyImage(replyId, file)} … />
 * ```
 */
export function useMediaAuthoring(token: string): UseMediaAuthoringResult {
  const uploadQuestionImage = useCallback(
    async (questionId: string, file: File): Promise<MediaUploadOutcome> => {
      if (!token) return { success: false, message: 'Not authenticated.' };
      try {
        const res = await adminApi.media.uploadQuestionImage(token, questionId, file);
        if (res.success) return { success: true, media: res.data.media };
        return {
          success: false,
          message: resolveMediaErrorMessage(res.error, {
            forbiddenMessage: MEDIA_AUTHOR_FORBIDDEN_MESSAGE,
          }),
        };
      } catch {
        return { success: false, message: UPLOAD_FALLBACK_MESSAGE };
      }
    },
    [token]
  );

  const uploadReplyImage = useCallback(
    async (replyId: string, file: File): Promise<MediaUploadOutcome> => {
      if (!token) return { success: false, message: 'Not authenticated.' };
      try {
        const res = await adminApi.media.uploadReplyImage(token, replyId, file);
        if (res.success) return { success: true, media: res.data.media };
        return {
          success: false,
          message: resolveMediaErrorMessage(res.error, {
            forbiddenMessage: MEDIA_AUTHOR_FORBIDDEN_MESSAGE,
          }),
        };
      } catch {
        return { success: false, message: UPLOAD_FALLBACK_MESSAGE };
      }
    },
    [token]
  );

  return useMemo(
    () => ({ uploadQuestionImage, uploadReplyImage }),
    [uploadQuestionImage, uploadReplyImage]
  );
}
